import { useState, useEffect } from "react";
import { Layout } from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import { fetchAPI } from "../lib/utils";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
	DialogDescription,
} from "../components/ui/dialog";
import { ScrollArea } from "../components/ui/scroll-area";
import { Progress } from "../components/ui/progress";
import {
	BookOpen,
	Upload,
	FileText,
	Trash2,
	Download,
	Eye,
	Plus,
	Search,
	FolderOpen,
} from "lucide-react";
import { toast } from "sonner";

export default function CoachLibrary() {
	const { user } = useAuth();
	const [documents, setDocuments] = useState([]);
	const [loading, setLoading] = useState(true);
	const [isUploadOpen, setIsUploadOpen] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");
	const [uploadProgress, setUploadProgress] = useState(0);
	const [isUploading, setIsUploading] = useState(false);

	const [newDocument, setNewDocument] = useState({
		title: "",
		description: "",
		category: "methodology",
		file: null,
	});

	const categories = [
		{ value: "methodology", label: "Metodologia" },
		{ value: "programming", label: "Programmazione" },
		{ value: "nutrition", label: "Nutrizione" },
		{ value: "recovery", label: "Recupero" },
		{ value: "reference", label: "Riferimenti" },
		{ value: "other", label: "Altro" },
	];

	useEffect(() => {
		loadDocuments();
	}, []);

	const loadDocuments = async () => {
		try {
			const data = await fetchAPI("/library");
			setDocuments(data);
		} catch (error) {
			console.error("Failed to load documents:", error);
		} finally {
			setLoading(false);
		}
	};

	const handleFileChange = (e) => {
		const file = e.target.files[0];
		if (file) {
			if (file.type !== "application/pdf") {
				toast.error("Solo file PDF sono supportati");
				return;
			}
			if (file.size > 10 * 1024 * 1024) {
				// 10MB limit
				toast.error("File troppo grande (max 10MB)");
				return;
			}
			setNewDocument({
				...newDocument,
				file,
				title: newDocument.title || file.name.replace(".pdf", ""),
			});
		}
	};

	const handleUpload = async () => {
		if (!newDocument.file || !newDocument.title) {
			toast.error("Seleziona un file e inserisci un titolo");
			return;
		}

		setIsUploading(true);
		setUploadProgress(0);

		try {
			// Create form data
			const formData = new FormData();
			formData.append("file", newDocument.file);
			formData.append("title", newDocument.title);
			formData.append("description", newDocument.description);
			formData.append("category", newDocument.category);

			// Simulate progress
			const progressInterval = setInterval(() => {
				setUploadProgress((prev) => Math.min(prev + 10, 90));
			}, 200);

			const response = await fetch(
				`${process.env.REACT_APP_BACKEND_URL}/api/library/upload`,
				{
					method: "POST",
					credentials: "include",
					headers: {
						Authorization: `Bearer ${localStorage.getItem("session_token") || ""}`,
					},
					body: formData,
				},
			);

			clearInterval(progressInterval);
			setUploadProgress(100);

			if (!response.ok) {
				const error = await response.json();
				throw new Error(error.detail || "Upload failed");
			}

			toast.success("Documento caricato con successo!");
			setIsUploadOpen(false);
			setNewDocument({
				title: "",
				description: "",
				category: "methodology",
				file: null,
			});
			loadDocuments();
		} catch (error) {
			toast.error("Errore nel caricamento: " + error.message);
		} finally {
			setIsUploading(false);
			setUploadProgress(0);
		}
	};

	const handleDelete = async (docId) => {
		if (!window.confirm("Eliminare questo documento?")) return;

		try {
			await fetchAPI(`/library/${docId}`, { method: "DELETE" });
			toast.success("Documento eliminato");
			loadDocuments();
		} catch (error) {
			toast.error("Errore: " + error.message);
		}
	};

	const filteredDocuments = documents.filter(
		(doc) =>
			doc.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
			doc.description?.toLowerCase().includes(searchQuery.toLowerCase()),
	);

	const getCategoryLabel = (value) => {
		return categories.find((c) => c.value === value)?.label || value;
	};

	return (
		<Layout>
			<div
				className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8"
				data-testid="library-page"
			>
				<div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
					<div>
						<h1 className="font-heading text-3xl md:text-4xl mb-2 flex items-center gap-3">
							<BookOpen className="w-8 h-8 text-volt" />
							Libreria Coach
						</h1>
						<p className="text-muted-foreground">
							Gestisci i tuoi materiali e metodologie di allenamento
						</p>
					</div>

					<Dialog open={isUploadOpen} onOpenChange={setIsUploadOpen}>
						<DialogTrigger asChild>
							<Button
								className="bg-volt hover:bg-volt/90 rounded-sm"
								data-testid="upload-doc-btn"
							>
								<Upload className="w-4 h-4 mr-2" />
								Carica Documento
							</Button>
						</DialogTrigger>
						<DialogContent className="bg-card border-border max-w-lg">
							<DialogDescription className="hidden" />
							<DialogHeader>
								<DialogTitle className="font-heading">
									Carica Documento
								</DialogTitle>
							</DialogHeader>
							<div className="space-y-4">
								{/* File Input */}
								<div className="space-y-2">
									<Label className="label-tactical">File PDF</Label>
									<div
										className={`border-2 border-dashed rounded-sm p-8 text-center cursor-pointer transition-colors ${
											newDocument.file
												? "border-volt bg-volt/5"
												: "border-border hover:border-volt/50"
										}`}
										onClick={() =>
											document.getElementById("file-input").click()
										}
									>
										<input
											id="file-input"
											type="file"
											accept=".pdf"
											onChange={handleFileChange}
											className="hidden"
										/>
										{newDocument.file ? (
											<div className="flex items-center justify-center gap-2">
												<FileText className="w-8 h-8 text-volt" />
												<div className="text-left">
													<p className="font-medium">{newDocument.file.name}</p>
													<p className="text-sm text-muted-foreground">
														{(newDocument.file.size / 1024 / 1024).toFixed(2)}{" "}
														MB
													</p>
												</div>
											</div>
										) : (
											<>
												<Upload className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
												<p className="text-sm text-muted-foreground">
													Clicca o trascina un file PDF qui
												</p>
												<p className="text-xs text-muted-foreground mt-1">
													Max 10MB
												</p>
											</>
										)}
									</div>
								</div>

								<div className="space-y-2">
									<Label className="label-tactical">Titolo *</Label>
									<Input
										value={newDocument.title}
										onChange={(e) =>
											setNewDocument({ ...newDocument, title: e.target.value })
										}
										className="bg-secondary border-transparent rounded-sm"
										placeholder="Nome del documento"
										data-testid="doc-title-input"
									/>
								</div>

								<div className="space-y-2">
									<Label className="label-tactical">Categoria</Label>
									<select
										value={newDocument.category}
										onChange={(e) =>
											setNewDocument({
												...newDocument,
												category: e.target.value,
											})
										}
										className="w-full bg-secondary border-transparent rounded-sm h-10 px-3 text-sm"
									>
										{categories.map((cat) => (
											<option key={cat.value} value={cat.value}>
												{cat.label}
											</option>
										))}
									</select>
								</div>

								<div className="space-y-2">
									<Label className="label-tactical">Descrizione</Label>
									<Textarea
										value={newDocument.description}
										onChange={(e) =>
											setNewDocument({
												...newDocument,
												description: e.target.value,
											})
										}
										className="bg-secondary border-transparent rounded-sm min-h-[80px]"
										placeholder="Breve descrizione del contenuto..."
									/>
								</div>

								{isUploading && (
									<div className="space-y-2">
										<Progress value={uploadProgress} className="h-2" />
										<p className="text-xs text-center text-muted-foreground">
											Caricamento in corso... {uploadProgress}%
										</p>
									</div>
								)}

								<Button
									onClick={handleUpload}
									disabled={isUploading || !newDocument.file}
									className="w-full bg-volt hover:bg-volt/90 rounded-sm"
									data-testid="upload-submit-btn"
								>
									{isUploading ? "Caricamento..." : "Carica Documento"}
								</Button>
							</div>
						</DialogContent>
					</Dialog>
				</div>

				{/* Search */}
				<div className="relative mb-6">
					<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
					<Input
						placeholder="Cerca documenti..."
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						className="pl-10 bg-secondary border-transparent h-12 rounded-sm"
						data-testid="library-search"
					/>
				</div>

				{/* Documents Grid */}
				{loading ? (
					<div className="flex items-center justify-center py-12">
						<div className="spinner"></div>
					</div>
				) : filteredDocuments.length > 0 ? (
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
						{filteredDocuments.map((doc) => (
							<Card
								key={doc.document_id}
								className="bg-card border-border rounded-sm hover:border-volt/30 transition-colors"
								data-testid={`doc-card-${doc.document_id}`}
							>
								<CardContent className="p-6">
									<div className="flex items-start justify-between mb-4">
										<div className="w-12 h-12 bg-volt/10 rounded-sm flex items-center justify-center">
											<FileText className="w-6 h-6 text-volt" />
										</div>
										<Button
											variant="ghost"
											size="icon"
											className="text-muted-foreground hover:text-blaze rounded-sm"
											onClick={() => handleDelete(doc.document_id)}
										>
											<Trash2 className="w-4 h-4" />
										</Button>
									</div>

									<p className="font-heading text-lg mb-1 line-clamp-1">
										{doc.title}
									</p>
									<span className="text-xs bg-secondary px-2 py-1 rounded-sm text-muted-foreground">
										{getCategoryLabel(doc.category)}
									</span>

									{doc.description && (
										<p className="text-sm text-muted-foreground mt-3 line-clamp-2">
											{doc.description}
										</p>
									)}

									<div className="flex items-center gap-2 mt-4 pt-4 border-t border-border">
										{doc.file_url && (
											<>
												<a
													href={doc.file_url}
													target="_blank"
													rel="noopener noreferrer"
													className="flex-1"
												>
													<Button
														variant="outline"
														className="w-full rounded-sm"
														size="sm"
													>
														<Eye className="w-4 h-4 mr-1" />
														Visualizza
													</Button>
												</a>
												<a href={doc.file_url} download>
													<Button
														variant="outline"
														size="icon"
														className="rounded-sm"
													>
														<Download className="w-4 h-4" />
													</Button>
												</a>
											</>
										)}
									</div>

									<p className="text-xs text-muted-foreground mt-3">
										{doc.file_size &&
											`${(doc.file_size / 1024 / 1024).toFixed(2)} MB`}
										{doc.created_at &&
											` • ${new Date(doc.created_at).toLocaleDateString("it-IT")}`}
									</p>
								</CardContent>
							</Card>
						))}
					</div>
				) : (
					<Card className="bg-card border-border rounded-sm">
						<CardContent className="flex flex-col items-center justify-center py-12 text-center">
							<FolderOpen className="w-12 h-12 text-muted-foreground mb-4" />
							<p className="font-heading text-lg mb-2">Libreria vuota</p>
							<p className="text-sm text-muted-foreground mb-4">
								{searchQuery
									? "Nessun documento corrisponde alla ricerca"
									: "Carica il tuo primo documento PDF"}
							</p>
							{!searchQuery && (
								<Button
									onClick={() => setIsUploadOpen(true)}
									className="bg-volt hover:bg-volt/90 rounded-sm"
								>
									<Upload className="w-4 h-4 mr-2" />
									Carica Documento
								</Button>
							)}
						</CardContent>
					</Card>
				)}
			</div>
		</Layout>
	);
}
